import { isPlatformBrowser } from "@angular/common";
import { Inject, Injectable, PLATFORM_ID } from "@angular/core";
import { Router } from "@angular/router";
import { MainState } from "@app/store/state";
import { LoginResponseInterface } from "@core/interfaces/auth/login-response.interface";
import { TwoFactorStatusInterface } from "@core/interfaces/auth/two-factor-status.interface";
import {
  TwoFactorVerificationRequestInterface,
  TwoFactorVerificationResponseInterface
} from "@core/interfaces/auth/two-factor-verification.interface";
import { BaseService } from "@core/services/base.service";
import { ClassicRoutesService } from "@core/services/classic-routes.service";
import { LoadingService } from "@core/services/loading.service";
import { UtilsService } from "@core/services/utils/utils.service";
import { WindowRefService } from "@core/services/window-ref.service";
import { Store } from "@ngrx/store";
import { CookieService } from "ngx-cookie";
import { Observable, of, BehaviorSubject, from } from "rxjs";
import { map, tap, take, catchError, switchMap } from "rxjs/operators";

import { AuthClassicApiService } from "./api/classic/auth/auth-classic-api.service";
import { AuthV2ApiService } from "./api/v2/auth/auth-v2-api.service";

@Injectable({
  providedIn: "root"
})
export class AuthService extends BaseService {
  static readonly CLASSIC_AUTH_TOKEN_COOKIE = "classic-auth-token";
  static readonly SESSION_TOKEN_KEY = "2fa-session-token";
  readonly isBrowser: boolean;

  private readonly _sessionToken$ = new BehaviorSubject<string | null>(null);
  private readonly _twoFactorMethod$ = new BehaviorSubject<"totp" | "email" | null>(null);
  private readonly _emailHint$ = new BehaviorSubject<string | null>(null);
  private readonly _pendingRedirectUrl$ = new BehaviorSubject<string | null>(null);

  constructor(
    public readonly store$: Store<MainState>,
    public readonly loadingService: LoadingService,
    public readonly authClassicApi: AuthClassicApiService,
    public readonly authV2Api: AuthV2ApiService,
    public readonly cookieService: CookieService,
    public readonly classicRoutesService: ClassicRoutesService,
    public readonly windowRefService: WindowRefService,
    public readonly utilsService: UtilsService,
    public readonly router: Router,
    @Inject(PLATFORM_ID) public readonly platformId: Object
  ) {
    super(loadingService);
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  getClassicApiToken(): string {
    return this.cookieService.get(AuthService.CLASSIC_AUTH_TOKEN_COOKIE);
  }

  login(handle: string, password: string, redirectUrl?: string): Observable<LoginResponseInterface> {
    if (redirectUrl) {
      this._pendingRedirectUrl$.next(redirectUrl);
    }
    return this.authV2Api.login(handle, password).pipe(
      tap(response => {
        if (response.requires_2fa) {
          this._sessionToken$.next(response.session_token);
          this._twoFactorMethod$.next(response.two_fa_method);
          this._emailHint$.next(response.email_hint || null);
        } else if (response.token) {
          this.storeAuthToken(response.token);
        }
      })
    );
  }

  loginClassic(handle: string, password: string): Observable<string> {
    return this.authClassicApi.login(handle, password);
  }

  verify2FA(otpCode: string): Observable<TwoFactorVerificationResponseInterface> {
    const sessionToken = this._sessionToken$.getValue();
    if (!sessionToken) {
      throw new Error("No session token available for 2FA verification");
    }

    const request: TwoFactorVerificationRequestInterface = {
      session_token: sessionToken,
      otp_code: otpCode
    };

    return this.authV2Api.verify2FA(request).pipe(
      tap(response => {
        if (response.token) {
          this.storeAuthToken(response.token);
          this.clear2FASession();
        }
      })
    );
  }

  get2FAStatus(): Observable<TwoFactorStatusInterface> {
    return this.authV2Api.get2FAStatus();
  }

  getSessionToken(): string | null {
    return this._sessionToken$.getValue();
  }

  getTwoFactorMethod(): "totp" | "email" | null {
    return this._twoFactorMethod$.getValue();
  }

  getEmailHint(): string | null {
    return this._emailHint$.getValue();
  }

  getPendingRedirectUrl(): string | null {
    return this._pendingRedirectUrl$.getValue();
  }

  clearPendingRedirectUrl(): void {
    this._pendingRedirectUrl$.next(null);
  }

  clear2FASession(): void {
    this._sessionToken$.next(null);
    this._twoFactorMethod$.next(null);
    this._emailHint$.next(null);
    // Don't clear redirect URL here - it should persist until login is complete
  }

  private storeAuthToken(token: string): void {
    const url = this.windowRefService.getCurrentUrl().toString();
    const options: any = {
      path: "/",
      expires: new Date(new Date().getTime() + 180 * 24 * 60 * 60 * 1000)
    };

    // Add domain based on current URL
    if (url?.includes("astrobin.com")) {
      options.domain = ".astrobin.com";
    } else if (url?.includes("localhost")) {
      options.domain = "localhost";
    }

    this.cookieService.put(AuthService.CLASSIC_AUTH_TOKEN_COOKIE, token, options);
    // Don't clear redirectUrl here - let it be cleared after the redirect happens
  }

  logout(): Observable<void> {
    // Navigate to logging-out page first
    return from(this.router.navigate(["account", "logging-out"])).pipe(
      switchMap(() => {
        const token = this.getClassicApiToken();

        // Best-effort API call to invalidate server-side session
        if (token) {
          this.authV2Api
            .logout()
            .pipe(
              take(1),
              catchError(error => {
                // Log error but continue with logout
                console.error("Logout API error:", error);
                return of(null);
              })
            )
            .subscribe();
        }

        // Clear all client-side authentication state
        this.clearAuthenticationState();

        // Small delay to show the logging out page, then navigate to logged-out page
        return from(this.utilsService.delay(1500)).pipe(
          switchMap(() => from(this.router.navigate(["account", "logged-out"]))),
          map(() => void 0)
        );
      })
    );
  }

  private clearAuthenticationState(): void {
    // Clear API token
    this.removeAuthenticationToken();

    // Clear 2FA session if any
    this.clear2FASession();

    // Clear pending redirect URL
    this._pendingRedirectUrl$.next(null);

    // Clear any user data from session storage
    if (this.isBrowser) {
      sessionStorage.clear();
    }
  }

  removeAuthenticationToken() {
    if (!this.isBrowser) {
      return;
    }

    // Try removing with no domain (how it's originally set)
    this.cookieService.remove(AuthService.CLASSIC_AUTH_TOKEN_COOKIE, {
      path: "/"
    });

    // Try removing from specific domains
    this.cookieService.remove(AuthService.CLASSIC_AUTH_TOKEN_COOKIE, {
      path: "/",
      domain: "www.astrobin.com"
    });

    this.cookieService.remove(AuthService.CLASSIC_AUTH_TOKEN_COOKIE, {
      path: "/",
      domain: "app.astrobin.com"
    });

    this.cookieService.remove(AuthService.CLASSIC_AUTH_TOKEN_COOKIE, {
      path: "/",
      domain: ".astrobin.com"
    });

    this.cookieService.remove(AuthService.CLASSIC_AUTH_TOKEN_COOKIE, {
      path: "/",
      domain: "localhost"
    });
  }

  isAuthenticated$(): Observable<boolean> {
    return of(!!this.getClassicApiToken());
  }
}
